import { Controller } from "@hotwired/stimulus";

// Intra-panel behaviour for the household merge resolution step: who comes
// along, which animals come, and who ends up head of household.
//
// The anchor's household always survives — staff are standing on it and the
// step reads as people moving in — so there is no direction to choose.
//
// Head of household is not a separate control. It is the `self` option in each
// row's role column, and this controller is what makes that safe: exactly one
// row may hold it, and the hidden head field is kept in sync so the server
// still receives an explicit head_neighbor_id.
const HEAD = "self";
const DEMOTED = "other_relative";

export default class extends Controller {
  static targets = [
    "personRow",
    "roleWrap",
    "roleSelect",
    "staysBehind",
    "headField",
    "headNote",
    "headWarning",
    "headWarningText",
  ];
  static values = {
    headWarning: String,
    demotionNote: String,
  };

  declare readonly personRowTargets: HTMLElement[];
  declare readonly headFieldTarget: HTMLInputElement;
  declare readonly hasHeadFieldTarget: boolean;
  declare readonly headNoteTarget: HTMLElement;
  declare readonly hasHeadNoteTarget: boolean;
  declare readonly headWarningTarget: HTMLElement;
  declare readonly hasHeadWarningTarget: boolean;
  declare readonly headWarningTextTarget: HTMLElement;
  declare readonly headWarningValue: string;
  declare readonly demotionNoteValue: string;

  connect() {
    this.render();
  }

  onMoveToggle() {
    this.render();
  }

  // Picking Head on one row takes it from whoever held it, the same demotion
  // Households::SetHead performs server-side. Without this the roster could
  // submit two heads, which a radio made impossible for free.
  onRoleChange(event: Event) {
    const select = event.target as HTMLSelectElement;

    if (select.value === HEAD) {
      this.roleSelects()
        .filter((other) => other !== select && other.value === HEAD)
        .forEach((other) => {
          other.value = DEMOTED;
        });
    }
    this.render();
  }

  private checkboxIn(row: HTMLElement): HTMLInputElement | null {
    return row.querySelector<HTMLInputElement>('input[type="checkbox"]');
  }

  private roleSelectIn(row: HTMLElement): HTMLSelectElement | null {
    return row.querySelector<HTMLSelectElement>("select");
  }

  private roleSelects(): HTMLSelectElement[] {
    return this.personRowTargets
      .map((row) => this.roleSelectIn(row))
      .filter((select): select is HTMLSelectElement => select !== null);
  }

  // People already in the surviving household render without a checkbox — the
  // absence of the control means the row is not optional, not that it is off.
  private moving(row: HTMLElement): boolean {
    const check = this.checkboxIn(row);
    return check ? check.checked : true;
  }

  private render() {
    this.personRowTargets.forEach((row) => {
      const moving = this.moving(row);
      row
        .querySelectorAll<HTMLElement>('[data-household-merge-target="roleWrap"]')
        .forEach((el) => el.classList.toggle("hidden", !moving));
      row
        .querySelectorAll<HTMLElement>('[data-household-merge-target="staysBehind"]')
        .forEach((el) => el.classList.toggle("hidden", moving));
      // A row on its way out must not keep describing this household — a
      // disabled select submits nothing.
      const select = this.roleSelectIn(row);
      if (select) select.disabled = !moving;
    });

    this.reseatHead();
    this.renderNotes();
  }

  // The head has to be someone who is actually in the household afterwards. If
  // they were just unchecked out of the move, the role falls back to a row that
  // is staying — never to nobody.
  private reseatHead() {
    // Anyone leaving cannot still be holding it.
    this.personRowTargets
      .filter((row) => !this.moving(row))
      .forEach((row) => {
        const select = this.roleSelectIn(row);
        if (select?.value === HEAD) select.value = DEMOTED;
      });

    const staying = this.personRowTargets.filter((row) => this.moving(row));
    const holder = staying.find((row) => this.roleSelectIn(row)?.value === HEAD);
    if (!holder) {
      // Prefer someone already in this household over an arriving one.
      const fallback =
        staying.find((row) => row.dataset.side === "anchor") ?? staying[0];
      const select = fallback && this.roleSelectIn(fallback);
      if (select) select.value = HEAD;
    }

    this.syncHeadField();
  }

  private syncHeadField() {
    if (!this.hasHeadFieldTarget) return;

    const holder = this.personRowTargets.find(
      (row) => this.moving(row) && this.roleSelectIn(row)?.value === HEAD,
    );
    if (holder?.dataset.personId) this.headFieldTarget.value = holder.dataset.personId;
  }

  private renderNotes() {
    if (this.hasHeadNoteTarget) this.headNoteTarget.textContent = this.demotionNote();
    this.renderHeadWarning();
  }

  // Handing the role to someone else demotes whoever was sitting in it. The row
  // dropdown already shows the new value; this says who it happened to.
  private demotionNote(): string {
    const sitting = this.personRowTargets.find(
      (row) => row.dataset.side === "anchor" && row.dataset.head === "true",
    );
    const sittingId = sitting?.dataset.personId;
    if (!sittingId || !this.hasHeadFieldTarget) return "";
    if (this.headFieldTarget.value === sittingId) return "";

    const name = sitting?.querySelector<HTMLElement>(".truncate")?.textContent?.trim();
    if (!name) return "";

    return this.demotionNoteValue.replace(/%\{name\}/g, name);
  }

  // The source household outlives the merge only if someone stays; if its head
  // leaves too, it is left without one on purpose.
  private renderHeadWarning() {
    if (!this.hasHeadWarningTarget) return;

    const arriving = this.personRowTargets.filter((row) => row.dataset.side === "source");
    const someoneStays = arriving.some((row) => !this.moving(row));
    const headLeaves = arriving.some(
      (row) => row.dataset.head === "true" && this.moving(row),
    );
    const show = someoneStays && headLeaves;

    this.headWarningTarget.classList.toggle("hidden", !show);
    if (show) this.headWarningTextTarget.textContent = this.headWarningValue;
  }
}
